import { formatDate, formatDateTime } from "../../../utils/date-formatting";
import type { ActivityEntry, ActivityFilter } from "./timeline-types";

/**
 * Count entries for each filter type
 */
export const countEntriesByFilter = (
	entries: ActivityEntry[]
): Record<ActivityFilter, number> => {
	const counts: Record<ActivityFilter, number> = {
		all: entries.length,
		notes: 0,
		attachments: 0,
		emails: 0,
		linkedin: 0,
		calls: 0,
		meetings: 0,
		votes: 0,
		board: 0,
		cooldowns: 0,
	};

	for (const entry of entries) {
		switch (entry.type) {
			case "note":
				counts.notes++;
				break;
			case "attachment":
				counts.attachments++;
				break;
			case "email_queued":
			case "email_sent":
			case "email_journal":
				counts.emails++;
				break;
			case "linkedin_queued":
			case "linkedin_sent":
			case "linkedin_conversation":
				counts.linkedin++;
				break;
			case "event":
				// All events (SentInvitation, Connection) count under LinkedIn
				counts.linkedin++;
				break;
			case "call":
				counts.calls++;
				break;
			case "meeting":
				counts.meetings++;
				break;
			case "vote":
				counts.votes++;
				break;
			case "board_movement":
				counts.board++;
				break;
			case "cooldown":
				counts.cooldowns++;
				break;
		}
	}

	return counts;
};

/**
 * Get the filter category for an entry
 */
export const getEntryCategory = (entry: ActivityEntry): ActivityFilter => {
	switch (entry.type) {
		case "note":
			return "notes";
		case "attachment":
			return "attachments";
		case "email_queued":
		case "email_sent":
		case "email_journal":
			return "emails";
		case "linkedin_queued":
		case "linkedin_sent":
		case "linkedin_conversation":
		case "event":
			// All events (SentInvitation, Connection) go under LinkedIn
			return "linkedin";
		case "call":
			return "calls";
		case "meeting":
			return "meetings";
		case "vote":
			return "votes";
		case "board_movement":
			return "board";
		case "cooldown":
			return "cooldowns";
	}
};

/**
 * Extract display text from an entry
 */
export function getEntryText(entry: ActivityEntry): string {
	switch (entry.type) {
		case "email_queued":
		case "email_sent":
			return entry.data.raw_text || "";
		case "linkedin_queued":
		case "linkedin_sent":
			return entry.data.text || "";
		case "linkedin_conversation":
			return entry.data.text || "";
		case "email_journal":
			return entry.data.text_display;
		case "vote": {
			const grade = `"${entry.data.grade}"`;
			if (entry.data.assignment_name) {
				return `Voted ${grade} for ${entry.data.assignment_name}`;
			}
			return `Voted ${grade}`;
		}
		case "event":
			return entry.data.description || "";
		case "board_movement": {
			const movement = entry.fromColumn
				? `${entry.fromColumn} → ${entry.data.column}`
				: entry.data.column;
			return entry.data.assignment_name
				? `${entry.data.assignment_name}: ${movement}`
				: movement;
		}
		case "note":
			return entry.data.comment;
		case "attachment":
			return `Uploaded: ${entry.data.name}`;
		case "call": {
			const outcome = entry.data.call_outcome
				? ` (${entry.data.call_outcome.replace("_", " ")})`
				: "";
			const duration = entry.data.duration_minutes
				? ` - ${entry.data.duration_minutes} min`
				: "";
			return (
				`${entry.data.content || ""}${outcome}${duration}`.trim() ||
				"Call logged"
			);
		}
		case "meeting": {
			const duration = entry.data.duration_minutes
				? ` - ${entry.data.duration_minutes} min`
				: "";
			return `${entry.data.content || ""}${duration}`.trim() || "Meeting logged";
		}
		case "cooldown":
			return `Cooldown until ${formatDate(entry.data.until)}`;
	}
}

/**
 * Extract subject from an entry (emails only)
 */
export function getEntrySubject(entry: ActivityEntry): string | null {
	switch (entry.type) {
		case "email_queued":
		case "email_sent":
			return entry.data.subject || null;
		case "email_journal":
			return entry.data.subject || null;
		default:
			return null;
	}
}

/**
 * Extract sender/recipient info from an entry
 */
export function getEntrySender(
	entry: ActivityEntry
): { prefix: string; name: string } | null {
	switch (entry.type) {
		case "email_queued":
		case "email_sent": {
			const name = entry.data.user_first_name;
			return name ? { prefix: "by", name } : null;
		}
		case "linkedin_queued":
		case "linkedin_sent": {
			const name = entry.data.user_first_name;
			return name ? { prefix: "by", name } : null;
		}
		case "linkedin_conversation": {
			// For inbound (replies from person), show which user received it
			// For outbound (sent by user), show which user sent it
			const name = entry.conversationUserFirstName;
			if (!name) return null;
			return entry.direction === "inbound"
				? { prefix: "to", name }
				: { prefix: "by", name };
		}
		case "email_journal": {
			// Show the user whose email account it was synced from
			// Outbound (sent): "by jimmy" - Inbound (received): "to jimmy"
			const name = entry.data.user_first_name;
			return name
				? {
						prefix: entry.direction === "outbound" ? "by" : "to",
						name,
					}
				: null;
		}
		case "vote": {
			const name = entry.data.user_first_name;
			return name ? { prefix: "by", name } : null;
		}
		case "event":
			return entry.data.user ? { prefix: "by", name: entry.data.user } : null;
		case "board_movement": {
			// Show who made the board change
			const name = entry.data.created_by_first_name;
			return name ? { prefix: "by", name } : null;
		}
		case "note": {
			const name = entry.data.user_first_name;
			return name ? { prefix: "by", name } : null;
		}
		case "attachment":
			return null; // No user info available for attachments
		case "call":
		case "meeting": {
			const name = entry.data.user_first_name;
			return name ? { prefix: "by", name } : null;
		}
		case "cooldown": {
			const name = entry.data.user_first_name;
			return name ? { prefix: "by", name } : null;
		}
	}
}

export interface EntryDate {
	label: string;
	/** Pre-formatted for display (date-only rows omit the meaningless time). */
	display: string;
	primary: boolean;
}

/**
 * All meaningful dates for an entry, in display order. `primary` marks the one
 * shown on the timeline line (bolded in the tooltip). Secondary dates that
 * render identically to an already-kept row are dropped as noise.
 *
 * `dateOnly` rows (e.g. a Connection's real LinkedIn date, which LinkedIn only
 * exposes at day precision — always 00:00 UTC) render without a time.
 */
export function getEntryDates(entry: ActivityEntry): EntryDate[] {
	const rows: { label: string; value: string; dateOnly: boolean }[] = [];
	const primary = entry.date;
	const add = (
		label: string,
		value: string | undefined | null,
		dateOnly = false
	): void => {
		if (value) rows.push({ label, value, dateOnly });
	};

	switch (entry.type) {
		case "email_queued":
			add("Enqueued", entry.data.created);
			break;
		case "email_sent":
			add("Sent", entry.data.sent_at);
			add("Enqueued", entry.data.created);
			break;
		case "linkedin_queued":
			add("Enqueued", entry.data.created);
			add("Updated", entry.data.updated);
			break;
		case "linkedin_sent":
			add("Sent", primary);
			add("Enqueued", entry.data.created);
			add("Updated", entry.data.updated);
			break;
		case "linkedin_conversation":
			add(
				entry.direction === "outbound" ? "Sent" : "Received",
				entry.data.created_at
			);
			add("Synced", entry.data.created);
			add("Updated", entry.data.updated);
			break;
		case "email_journal":
			add(entry.direction === "outbound" ? "Sent" : "Received", entry.data.date);
			add("Synced", entry.data.created);
			break;
		case "vote":
			add("Voted", entry.data.created);
			break;
		case "event":
			if (entry.data.model === "Connection") {
				add("Connected", entry.data.connected, true);
				add("Synced", new Date(entry.data.timestamp).toISOString());
			} else if (entry.data.model === "SentInvitation") {
				add("Sent", primary);
				add("Enqueued", entry.data.enqueued);
			} else {
				add("When", primary);
			}
			break;
		case "board_movement":
			add("Moved", entry.data.created);
			break;
		case "note":
			add("Posted", entry.data.submit_date);
			break;
		case "attachment":
			add("Uploaded", entry.data.created);
			add("Updated", entry.data.updated);
			break;
		case "call":
		case "meeting":
			add("Happened", entry.data.happened_at);
			add("Logged", entry.data.created);
			add("Updated", entry.data.updated);
			break;
		case "cooldown":
			add("Set", entry.data.created);
			add("Until", entry.data.until);
			break;
	}

	// The primary row is the one holding the timeline's own sort date; format it
	// (date-only rows drop the meaningless time) and place it first.
	const fmt = (r: { value: string; dateOnly: boolean }): string =>
		r.dateOnly ? formatDate(r.value) : formatDateTime(r.value);
	const primaryIdx = rows.findIndex((r) => r.value === primary);
	const seen = new Set<string>();
	const deduped: EntryDate[] = [];
	if (primaryIdx >= 0) {
		const text = fmt(rows[primaryIdx]);
		seen.add(text);
		deduped.push({ label: rows[primaryIdx].label, display: text, primary: true });
	} else {
		const text = formatDateTime(primary);
		seen.add(text);
		deduped.push({ label: "Timeline", display: text, primary: true });
	}
	// Secondaries in original order; drop any that render identically to a kept
	// row (so e.g. "Updated" is hidden when it formats the same as created).
	for (let i = 0; i < rows.length; i++) {
		if (i === primaryIdx) continue;
		const text = fmt(rows[i]);
		if (seen.has(text)) continue;
		seen.add(text);
		deduped.push({ label: rows[i].label, display: text, primary: false });
	}
	return deduped;
}

/**
 * Get debug identifier for an entry
 */
export function getEntryDebugId(entry: ActivityEntry): string {
	switch (entry.type) {
		case "email_queued":
		case "email_sent":
			// Include email_journal_id to debug deduplication
			return `EmailQueue:${entry.data.id}→EJ:${entry.data.email_journal_id ?? "null"}`;
		case "linkedin_queued":
		case "linkedin_sent":
			return `LinkedinQueue:${entry.data.id}→CC:${entry.data.cc_id ?? "null"}`;
		case "linkedin_conversation":
			return `ConversationContent:${entry.data.id}`;
		case "email_journal":
			return `EmailJournal:${entry.data.email_journal_id}`;
		case "vote":
			return `Vote:${entry.data.id}`;
		case "event":
			return `${entry.data.model}:${entry.data.id}`;
		case "board_movement":
			return `PA:${entry.data.id}`;
		case "note":
			return `Comment:${entry.data.id}`;
		case "attachment":
			return `Attachment:${entry.data.id}`;
		case "call":
		case "meeting":
			return `Activity:${entry.data.id}`;
		case "cooldown":
			return `Cooldown:${entry.data.id}`;
	}
}
